/**
 * iter90 · Strategia C · In-memory API cache with TTL + in-flight dedupe.
 *
 * Purpose (see /app/memory/perf_diagnosis.md "Section Navigation Diagnosis
 * pass 4"): eliminate the ~9-16s API round-trip on first section navigation
 * by warming a cache during Home idle time and on link hover/touch. The
 * SectionPage `useEffect` fetch then reads from the cache instead of doing
 * a fresh network round-trip.
 *
 * Design decisions:
 *  - Map-based (no persistence to localStorage / sessionStorage). Cache
 *    lives only for the current tab session. Reload = fresh fetch. This
 *    is intentional to keep CMS edits visible after a manual reload.
 *  - TTL 5 min. Long enough to cover a typical browsing burst, short
 *    enough that a returning user gets fresh content.
 *  - In-flight dedupe: two concurrent `fetch(url)` calls yield ONE
 *    network request. The second caller awaits the first Promise.
 *  - Admin bypass: entries with `/admin/` prefix are refused (admin uses
 *    a separate axios instance `admin` in adminApi.js — this is
 *    belt-and-suspenders in case a public caller happens to hit an
 *    admin URL).
 *  - Video bypass: `.mp4`, `.webm`, `.m3u8` URLs and content-type
 *    `video/*` are never cached (the cache is for JSON data, not blobs).
 *  - `invalidate(prefix)` public API for CMS-triggered refresh.
 */

const TTL_MS = 5 * 60 * 1000; // 5 minutes

// key = fullUrl, value = { data, timestamp } (settled) OR { promise, timestamp } (in-flight)
const cache = new Map();

function isVideoUrl(url) {
    if (typeof url !== 'string') return false;
    const lower = url.toLowerCase();
    return /\.(mp4|webm|m3u8|mov|ogv)(\?|$)/.test(lower);
}

function isAdminUrl(url) {
    if (typeof url !== 'string') return false;
    return url.includes('/admin/') || url.includes('/auth/');
}

function isFresh(entry) {
    if (!entry) return false;
    return Date.now() - entry.timestamp < TTL_MS;
}

export const apiCache = {
    /**
     * Synchronous peek. Returns cached data if fresh, else undefined.
     * Does NOT wait for in-flight promises (use `fetch()` for that).
     */
    get(url) {
        const entry = cache.get(url);
        if (!entry || !isFresh(entry)) return undefined;
        return entry.data !== undefined ? entry.data : undefined;
    },

    /**
     * Fetch with dedupe + TTL cache. `fetcher` is a callable that
     * returns a Promise<data> and receives no arguments. It's only
     * invoked on cache miss + no in-flight request.
     *
     * Returns Promise<data>.
     */
    fetch(url, fetcher) {
        if (isVideoUrl(url) || isAdminUrl(url)) {
            // Bypass cache entirely.
            return fetcher();
        }
        const entry = cache.get(url);
        if (entry && isFresh(entry)) {
            if (entry.promise) return entry.promise; // in-flight dedupe
            return Promise.resolve(entry.data);
        }
        // Cache miss or expired → start a new fetch.
        const promise = fetcher()
            .then((data) => {
                cache.set(url, { data, timestamp: Date.now() });
                return data;
            })
            .catch((err) => {
                // Failure: remove in-flight marker so the next call
                // can retry rather than getting a stuck-pending promise.
                cache.delete(url);
                throw err;
            });
        cache.set(url, { promise, timestamp: Date.now() });
        return promise;
    },

    /**
     * Invalidate a specific URL or every URL starting with `prefix`.
     * Intended for CMS admin flows (post-save refresh) — public code
     * generally shouldn't call this.
     */
    invalidate(urlOrPrefix) {
        if (typeof urlOrPrefix !== 'string' || !urlOrPrefix) {
            cache.clear();
            return;
        }
        // Exact match first
        if (cache.has(urlOrPrefix)) {
            cache.delete(urlOrPrefix);
            return;
        }
        // Prefix match
        for (const key of cache.keys()) {
            if (key.startsWith(urlOrPrefix)) cache.delete(key);
        }
    },

    /** Test helper — total number of live entries. */
    size() {
        return cache.size;
    },
};

export default apiCache;

// iter90 · Dev-only debug hook: expose to `window` when running in
// non-production so Playwright tests can introspect cache state.
if (typeof window !== 'undefined' && process.env.NODE_ENV !== 'production') {
    window.__araia_apiCache = apiCache;
}
